import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Activity, Clock, LayoutGrid, QrCode, Sparkles, TrendingUp, Users } from "lucide-react";
import { Orb } from "@/components/Orb";
import { PLMJLogo } from "@/components/PLMJLogo";
import { QRBlock } from "@/components/QRBlock";
import { getStats, listTeammates } from "@/lib/api";
import type { FunctionCategory, Stats, TeammateSummary } from "@/lib/types";
import { cn } from "@/lib/cn";

type TabId = "qr" | "feed" | "orb" | "marketplace" | "stats";

const TABS: { id: TabId; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: "qr", label: "Scan", icon: QrCode },
  { id: "feed", label: "Live feed", icon: Activity },
  { id: "orb", label: "Presence", icon: Sparkles },
  { id: "marketplace", label: "Marketplace", icon: LayoutGrid },
  { id: "stats", label: "Results", icon: TrendingUp },
];

const PRACTICE_LABEL: Record<FunctionCategory, string> = {
  corporate_ma: "Corporate / M&A",
  litigation: "Dispute Resolution",
  tax: "Tax",
  labour: "Employment",
  real_estate: "Real Estate",
  banking_finance: "Banking & Finance",
  ip: "IP",
  competition: "Competition",
  tmt: "TMT",
  public_law: "Public Law",
  restructuring: "Restructuring",
  compliance: "Compliance",
  other: "Other",
};

export default function Stage() {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<TabId>((params.get("tab") as TabId) || "qr");

  const [stats, setStats] = useState<Stats | null>(null);
  const [teammates, setTeammates] = useState<TeammateSummary[]>([]);
  const [pulseKey, setPulseKey] = useState(0);
  const lastCountRef = useRef(0);

  const refresh = useCallback(async () => {
    try {
      const [s, list] = await Promise.all([getStats("all"), listTeammates({ limit: 60 })]);
      setStats(s);
      setTeammates(list);
      if (lastCountRef.current && s.total > lastCountRef.current) {
        setPulseKey((k) => k + 1);
      }
      lastCountRef.current = s.total;
    } catch {
      /* keep last state */
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 2500);
    return () => clearInterval(t);
  }, [refresh]);

  useEffect(() => {
    const next = new URLSearchParams(params);
    next.set("tab", tab);
    setParams(next, { replace: true });
  }, [tab, params, setParams]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "1") setTab("qr");
      else if (e.key === "2") setTab("feed");
      else if (e.key === "3") setTab("orb");
      else if (e.key === "4") setTab("marketplace");
      else if (e.key === "5") setTab("stats");
      else if (e.key.toLowerCase() === "f") {
        if (document.fullscreenElement) document.exitFullscreen();
        else document.documentElement.requestFullscreen();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://plmj.ruicosta.ai";
  const qrUrl = `${origin}/?go=build`;
  const total = stats?.total ?? 0;
  const totalHours = stats?.totalHoursSaved ?? 0;

  return (
    <div className="relative min-h-dvh overflow-hidden">
      <AmbientBackground pulseKey={pulseKey} />
      <div className="relative z-10 mx-auto flex min-h-dvh max-w-[1440px] flex-col px-10 py-8">
        <TopBar total={total} totalHours={totalHours} pulseKey={pulseKey} />

        <div className="mt-8 flex flex-1 items-stretch">
          <AnimatePresence mode="wait">
            {tab === "qr" && <QRTab key="qr" url={qrUrl} />}
            {tab === "feed" && <FeedTab key="feed" teammates={teammates} />}
            {tab === "orb" && (
              <OrbTab key="orb" pulseKey={pulseKey} total={total} hours={totalHours} />
            )}
            {tab === "marketplace" && <MarketplaceTab key="mkt" teammates={teammates} />}
            {tab === "stats" && <StatsTab key="stats" stats={stats} />}
          </AnimatePresence>
        </div>

        <TabBar active={tab} onChange={setTab} />
      </div>
    </div>
  );
}

function AmbientBackground({ pulseKey }: { pulseKey: number }) {
  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(1200px 900px at 85% -20%, rgba(94,234,255,0.14), transparent 60%),radial-gradient(1100px 800px at 15% 120%, rgba(28,127,255,0.22), transparent 60%)",
        }}
      />
      <AnimatePresence>
        <motion.div
          key={pulseKey}
          className="pointer-events-none absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.45, 0] }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.8, ease: "easeOut" }}
          style={{
            background:
              "radial-gradient(800px 600px at 50% 50%, rgba(94,234,255,0.35), transparent 70%)",
          }}
        />
      </AnimatePresence>
    </>
  );
}

function TopBar({
  total,
  totalHours,
  pulseKey,
}: {
  total: number;
  totalHours: number;
  pulseKey: number;
}) {
  return (
    <div className="flex items-center gap-6">
      <div className="flex items-center gap-4">
        <PLMJLogo className="text-[28px]" />
        <div className="h-8 w-px bg-white/10" />
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-white/50">
            PLMJ · Build your own Harvey
          </div>
          <div className="text-2xl font-semibold leading-none text-white">AI Associate Builder</div>
        </div>
      </div>

      <div className="ml-auto flex items-center gap-3">
        <motion.div
          key={pulseKey + "-counter"}
          initial={{ scale: 1 }}
          animate={{ scale: [1, 1.08, 1] }}
          transition={{ duration: 0.6 }}
          className="glass glow-border flex items-center gap-3 rounded-full px-5 py-2"
        >
          <Users className="h-4 w-4 text-cyan-glow" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50">Associates</div>
            <div className="text-2xl font-semibold leading-none text-white">{total}</div>
          </div>
        </motion.div>

        <div className="glass flex items-center gap-3 rounded-full px-5 py-2">
          <Clock className="h-4 w-4 text-cyan-glow" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50">Hours saved / week</div>
            <div className="text-2xl font-semibold leading-none text-white">{totalHours}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TabBar({ active, onChange }: { active: TabId; onChange: (t: TabId) => void }) {
  return (
    <div className="mt-8 flex justify-center">
      <div className="glass flex items-center gap-1 rounded-full p-1.5">
        {TABS.map(({ id, label, icon: Icon }, idx) => (
          <button
            key={id}
            onClick={() => onChange(id)}
            className={cn(
              "flex items-center gap-2 rounded-full px-5 py-2.5 text-sm transition",
              active === id ? "bg-white text-ink-900" : "text-white/60 hover:text-white",
            )}
          >
            <Icon className="h-4 w-4" />
            {label}
            <span
              className={cn(
                "ml-1 rounded px-1.5 py-0.5 text-[10px]",
                active === id ? "bg-ink-900/10 text-ink-900/50" : "bg-white/5 text-white/40",
              )}
            >
              {idx + 1}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function QRTab({ url }: { url: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="grid w-full grid-cols-1 items-center gap-12 lg:grid-cols-2"
    >
      <div className="flex items-center justify-center">
        <QRBlock url={url} size={360} label="Scan with your phone camera" />
      </div>
      <div>
        <div className="inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-widest text-white/60">
          PLMJ · Live demo · 3 minutes
        </div>
        <h1 className="mt-5 text-7xl font-semibold leading-[0.95] text-white">
          Build your own
          <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-glow to-brand-400">
            Harvey.
          </span>
        </h1>
        <p className="mt-6 max-w-lg text-xl leading-relaxed text-white/70">
          Take 3 minutes. Scan the QR. Design an AI associate that handles the first 80% of one recurring task in your practice.
        </p>
        <ul className="mt-6 space-y-2 text-base text-white/80">
          <li className="flex items-center gap-2">
            <span className="text-cyan-glow">•</span> Pick a recurring task you do every week
          </li>
          <li className="flex items-center gap-2">
            <span className="text-cyan-glow">•</span> Get a reusable prompt + a live sample run
          </li>
          <li className="flex items-center gap-2">
            <span className="text-cyan-glow">•</span> See hours saved per week — pilot it Monday morning
          </li>
        </ul>
      </div>
    </motion.div>
  );
}

function FeedTab({ teammates }: { teammates: TeammateSummary[] }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="w-full"
    >
      <div className="mb-4 flex items-center gap-3">
        <div className="flex items-center gap-1.5 rounded-full bg-cyan-glow/10 px-3 py-1 text-xs text-cyan-glow">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-glow/80" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-cyan-glow" />
          </span>
          live feed
        </div>
        <div className="text-sm text-white/40">AI associates being built right now</div>
      </div>

      <div className="no-scrollbar max-h-[64vh] overflow-y-auto pr-2">
        <TeammateGrid teammates={teammates} />
      </div>
    </motion.div>
  );
}

function TeammateGrid({ teammates }: { teammates: TeammateSummary[] }) {
  if (teammates.length === 0) return <EmptyState label="Waiting for the first associate…" />;
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
      <AnimatePresence initial={false}>
        {teammates.map((t) => {
          const practice = PRACTICE_LABEL[t.function] || t.function;
          return (
            <motion.a
              key={t.id}
              href={`/t/${t.id}`}
              target="_blank"
              rel="noopener noreferrer"
              layout
              initial={{ opacity: 0, y: 20, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -3, scale: 1.01 }}
              className="glass block rounded-lg p-5 transition-colors hover:bg-white/[0.06]"
              style={{ borderColor: `${t.accent}33` }}
            >
              <div className="flex items-start gap-3">
                <div
                  className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-2xl"
                  style={{ background: `${t.accent}22` }}
                >
                  {t.emoji}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-lg font-semibold text-white">{t.name}</div>
                  <div className="truncate text-xs text-white/50">
                    {practice} · {t.role}
                    {t.creatorName && (
                      <span className="ml-1 text-white/40">· by {t.creatorName}</span>
                    )}
                  </div>
                </div>
                {t.hoursSaved > 0 && (
                  <span
                    className="rounded-full px-2 py-0.5 text-[10px] font-medium text-white"
                    style={{ background: `${t.accent}33` }}
                  >
                    +{t.hoursSaved}h/wk
                  </span>
                )}
              </div>
              <p className="mt-3 line-clamp-3 text-sm leading-snug text-white/75">{t.mission}</p>
            </motion.a>
          );
        })}
      </AnimatePresence>
    </div>
  );
}

function OrbTab({
  pulseKey,
  total,
  hours,
}: {
  pulseKey: number;
  total: number;
  hours: number;
}) {
  const [intensity, setIntensity] = useState<"idle" | "pulse">("idle");
  useEffect(() => {
    if (!pulseKey) return;
    setIntensity("pulse");
    const t = setTimeout(() => setIntensity("idle"), 2000);
    return () => clearTimeout(t);
  }, [pulseKey]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="flex w-full flex-col items-center justify-center gap-10 text-center"
    >
      <Orb size={520} intensity={intensity} />
      <div>
        <div className="text-sm uppercase tracking-[0.3em] text-white/40">PLMJ · live presence</div>
        <h2 className="mt-3 text-6xl font-semibold leading-tight text-white">
          <span className="text-cyan-glow">{total}</span> associates,{" "}
          <span className="text-cyan-glow">{hours}</span> hours / week
        </h2>
        <p className="mt-4 max-w-xl text-lg text-white/60">
          Every pulse is a new associate being born in this room.
        </p>
      </div>
    </motion.div>
  );
}

function MarketplaceTab({ teammates }: { teammates: TeammateSummary[] }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="w-full"
    >
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-semibold text-white">Associate Marketplace</h2>
          <p className="text-sm text-white/60">
            Every associate built today — browse, borrow, remix across practices.
          </p>
        </div>
        <div className="text-xs text-white/40">{teammates.length} shown</div>
      </div>
      <div className="no-scrollbar max-h-[62vh] overflow-y-auto pr-2">
        <TeammateGrid teammates={teammates} />
      </div>
    </motion.div>
  );
}

function StatsTab({ stats }: { stats: Stats | null }) {
  const functionsSorted = useMemo(() => {
    if (!stats) return [];
    return Object.entries(stats.byFunction)
      .filter(([, n]) => n > 0)
      .sort((a, b) => b[1] - a[1]);
  }, [stats]);
  const max = functionsSorted[0]?.[1] ?? 1;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="w-full"
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Stat label="Associates built" value={stats?.total ?? 0} highlight />
        <Stat label="Hours saved / week" value={stats?.totalHoursSaved ?? 0} />
        <Stat label="Practice areas" value={functionsSorted.length} />
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="glass rounded-xl p-6">
          <h3 className="text-sm uppercase tracking-widest text-white/60">By practice</h3>
          <div className="mt-4 space-y-3">
            {functionsSorted.length === 0 && (
              <div className="text-sm text-white/40">Waiting for data…</div>
            )}
            {functionsSorted.map(([f, n]) => (
              <div key={f}>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="text-white/80">
                    {PRACTICE_LABEL[f as FunctionCategory] || f}
                  </span>
                  <span className="font-mono text-white/60">{n}</span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/5">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(n / max) * 100}%` }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="h-full rounded-full bg-gradient-to-r from-cyan-glow to-brand-500"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="glass rounded-xl p-6">
          <h3 className="text-sm uppercase tracking-widest text-white/60">Popular names</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {(stats?.topNames ?? []).slice(0, 16).map((n) => (
              <span
                key={n.name}
                className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/80"
                style={{ fontSize: `${Math.min(22, 11 + n.count * 2)}px` }}
              >
                {n.name}
              </span>
            ))}
            {(stats?.topNames ?? []).length === 0 && (
              <div className="text-sm text-white/40">Waiting for the first associate…</div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number;
  highlight?: boolean;
}) {
  return (
    <div className={cn("glass rounded-xl p-6", highlight && "glow-border")}>
      <div className="text-xs uppercase tracking-widest text-white/50">{label}</div>
      <div className="mt-2 text-6xl font-semibold leading-none text-white">{value}</div>
    </div>
  );
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-64 flex-col items-center justify-center text-center text-white/40">
      <Orb size={100} intensity="idle" />
      <p className="mt-6 text-sm">{label}</p>
    </div>
  );
}
